import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { View, Text, ScrollView, Platform, Linking, TouchableOpacity } from 'react-native';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import { useRouter } from 'expo-router';
import Animated, { FadeIn } from 'react-native-reanimated';
import LottieView from 'lottie-react-native';
import * as Crypto from 'expo-crypto';
import SolidButton from '@/components/General/SolidButton';
import Unicon from '@/components/General/Unicon';
import Callout from '@/components/General/Callout';
import FieldRenderer from './FieldRenderer';
import FormResources from './FormResources';
import FormPaymentCheckout from './FormPaymentCheckout';
import { generateFormSubmissionID } from '@/data/formPayments';
import {
    validateEmail,
    validatePhoneUS,
    normalizePhoneUS,
    validateAddress,
} from '@/utils/InputValidator';
import { isSignatureDraftEmpty, validateSignatureDraft, validateProfileValue } from '@/utils/formSignaturePolicy';

const COMPLETE_LOTTIE = require('@/assets/Complete_lottie.json');

const FILE_TYPES = new Set(['file']);

// Signatures are per-signer evidence and must never be copied between
// repeated-section instances; files carry shared upload paths.
const COPY_PREVIOUS_EXCLUDED = new Set(['file', 'signature']);

const getRepeatFieldId = (fieldId, sectionId, index) => `${fieldId}:${sectionId}:${index}`;

/**
 * Builds the initial values map from `initialValues` first, then falls back to
 * each field's `defaultValue`. Flattened values for repeated-section instances
 * are stored under synthetic keys so FieldRenderer can stay unchanged.
 */
const buildInitialValues = (sections, initialValues) => {
    const values = {};
    for (const section of sections || []) {
        const repeatInstances = section.allowAddMore ? (initialValues?.__repeats__?.[section.id] || []) : [];
        const repeatCount = repeatInstances.length;
        for (const field of section.fields || []) {
            if (initialValues && initialValues[field.fieldId] !== undefined) {
                values[field.fieldId] = initialValues[field.fieldId];
            } else if (field.defaultValue !== undefined && field.defaultValue !== null) {
                values[field.fieldId] = field.defaultValue;
            } else {
                values[field.fieldId] = null;
            }
            for (let i = 1; i <= repeatCount; i++) {
                const instance = repeatInstances[i - 1] || {};
                const instanceKey = getRepeatFieldId(field.fieldId, section.id, i);
                if (instance[field.fieldId] !== undefined) {
                    values[instanceKey] = instance[field.fieldId];
                } else if (field.defaultValue !== undefined && field.defaultValue !== null) {
                    values[instanceKey] = field.defaultValue;
                } else {
                    values[instanceKey] = null;
                }
            }
        }
    }
    return values;
};

const buildInitialRepeatCounts = (sections, initialValues) => {
    const counts = {};
    for (const section of sections || []) {
        if (section.allowAddMore) {
            const arr = initialValues?.__repeats__?.[section.id];
            counts[section.id] = Array.isArray(arr) ? arr.length : 0;
        }
    }
    return counts;
};

const validateFieldValue = (field, value) => {
    if (field.hidden) return '';
    const required = !!field.required;
    const config = field.config || {};

    // Signature drafts are structured objects — generic isEmpty() would never
    // treat them as empty because `kind` is always set. An untouched signature
    // is null; a draft with no captured image is empty; a partial draft is
    // invalid per the signature consent policy.
    if (field.type === 'signature') {
        const empty = isSignatureDraftEmpty(value);
        if (required && empty) return `${field.label || 'Signature'} is required.`;
        if (empty) return '';
        return validateSignatureDraft(field, value);
    }

    // Profile answers are exactly `null` or `{ personID }` — a raw person
    // object or array must be rejected here, never serialized into the payload.
    if (field.type === 'profile') {
        return validateProfileValue(field, value);
    }

    if (required && isEmpty(value)) {
        return `${field.label || 'This field'} is required.`;
    }
    if (isEmpty(value)) return '';

    switch (field.type) {
        case 'payment':
            if (!['inPerson', 'electronic', 'skip'].includes(value?.channel)) return 'Choose a payment option.';
            if (required && value.channel === 'skip') return 'Payment is required.';
            if (['inPerson', 'electronic'].includes(value.channel) && Array.isArray(config.amountOptions) && config.amountOptions.length > 0 && !config.amountOptions.some((o) => o.id === value.optionId)) return 'Choose a payment amount.';
            if (value.channel !== 'skip' && config.allowedChannels !== 'both' && config.allowedChannels !== value.channel) return 'This payment option is not allowed.';
            if (value.channel === 'electronic' && (!value.paymentMethodID || !Number.isInteger(value.expectedGrossAmount))) return 'Add and select a payment method.';
            if (value.channel === 'electronic' && (!value.billingContact?.name?.trim() || !validateEmail(value.billingContact?.email))) return 'Enter the payer name and valid email.';
            break;
        case 'email':
            if (!validateEmail(value)) return 'Enter a valid email address.';
            break;
        case 'phone':
            if (!validatePhoneUS(value)) return 'Enter a valid US phone number.';
            break;
        case 'number': {
            if (typeof value !== 'number' || Number.isNaN(value)) {
                return 'Enter a valid number.';
            }
            if (config.min != null && value < config.min) return `Must be at least ${config.min}.`;
            if (config.max != null && value > config.max) return `Must be at most ${config.max}.`;
            if (typeof config.step === 'number' && config.step > 0) {
                const min = typeof config.min === 'number' ? config.min : 0;
                const remainder = (value - min) / config.step;
                if (Math.abs(remainder - Math.round(remainder)) > 1e-9) return `Must be in increments of ${config.step}.`;
            }
            break;
        }
        case 'date':
        case 'datetime':
        case 'time':
            if (Number.isNaN(new Date(value).getTime())) return field.type === 'time' ? 'Enter a valid time.' : 'Enter a valid date.';
            if (config.min && new Date(value) < new Date(config.min)) return `Must be on or after ${config.min}.`;
            if (config.max && new Date(value) > new Date(config.max)) return `Must be on or before ${config.max}.`;
            break;
        case 'dropdown': {
            const allowed = (config.options || []).map((o) => (typeof o === 'object' ? o.value : o));
            if (!allowed.includes(value)) return 'Select a valid option.';
            break;
        }
        case 'checkboxGroup': {
            const allowed = (config.options || []).map((o) => (typeof o === 'object' ? o.value : o));
            const selected = Array.isArray(value) ? value : [];
            if (!selected.every((v) => allowed.includes(v))) return 'Select valid options.';
            break;
        }
        case 'address':
            if (!validateAddress(value)) return 'Enter a complete US address.';
            break;
        default:
            break;
    }
    return '';
};

const isEmpty = (value) => {
    if(value === null || value === undefined) return true;
    if(typeof value === 'string') return value.trim() === '';
    if(Array.isArray(value)) return value.length === 0;
    if(typeof value === 'object') {
        // address/name objects: empty if every key is empty
        return Object.values(value).every((v) => isEmpty(v));
    }
    return false;
};

/**
 * Shared form renderer used by both the public route and the in-app embed.
 *
 * Props:
 * - form: form object with `schema`, `title`, `description`, `submitButtonText`,
 *   `successMessage`, `status`
 * - initialValues: optional map of fieldId -> value
 * - onSubmit(payload, files): called with validated values + a map of file
 *   field values. May return a Promise<{ successMessage, redirectUrl }>.
 * - onSuccess(result): optional callback invoked with the submit result
 * - loggedInUser: optional person object, passed to NameField for autofill
 * - isPublic: bool — controls spam-protection fields (honeypot, form_loaded_at,
 *   hCaptcha token slot). NOTE: this is a render-context hint only — permission
 *   gating (e.g. profile fields) always reads `form.isPublic`.
 * - disabled: bool — read-only mode (entry review)
 * - renderMode: 'fill' | 'preview' | 'submission' — explicit rendering intent.
 *   'submission' renders persisted answer evidence (signature images, profile
 *   cards) instead of editable inputs. `disabled` remains as a legacy
 *   read-only switch; both imply no editing.
 * - captchaToken: optional hCaptcha token string (public forms)
 * - renderCaptcha: optional render prop for the hCaptcha widget
 * - onFormChanged(): optional callback fired when submit-form returns
 *   409 `form_changed` — the caller should refetch the form so the new
 *   contentRevision reaches `formContext` (FormView itself strips
 *   signature/consent answers and keeps everything else).
 * - scrollable: bool — when false, renders content in a plain View so the
 *   parent can own scrolling (e.g. the public form page scrolls its header,
 *   form, and footer together)
 */
const FormView = ({
    form,
    initialValues,
    onSubmit,
    onSuccess,
    onFormChanged = null,
    loggedInUser,
    isPublic = false,
    publicId = null,
    disabled = false,
    renderMode = 'fill',
    captchaToken = null,
    renderCaptcha = null,
    scrollable = true,
}) => {
    // Explicit render mode is the intent; `disabled` is the legacy switch.
    const isReadOnly = disabled || renderMode !== 'fill';
    const schema = form?.schema || { sections: [] };
    const sections = schema.sections || [];

    const { theme: { colors, fonts } } = useUnistyles();
    const router = useRouter();

    const [values, setValues] = useState(() => buildInitialValues(sections, initialValues));
    const [repeats, setRepeats] = useState(() => buildInitialRepeatCounts(sections, initialValues));
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [serverError, setServerError] = useState(null);
    const [successResult, setSuccessResult] = useState(null);
    const [honeypot, setHoneypot] = useState('');
    const [formLoadedAt, setFormLoadedAt] = useState(null);
    const lottieRef = useRef(null);
    const submissionID = useRef(Crypto.randomUUID());
    // Tracks in-flight signature exports — submit is blocked while any pad
    // export is pending so we never submit without an image.
    const pendingSignatures = useRef(new Set());
    // Bumped on a 409 form_changed — SignatureField pads read this to discard
    // ink left over from terms that no longer exist.
    const [signatureResetEpoch, setSignatureResetEpoch] = useState(0);

    const onSignaturePending = useCallback((key, pending) => {
        if (pending) pendingSignatures.current.add(key);
        else pendingSignatures.current.delete(key);
    }, []);

    // Shared field context. `formIsPublic` reads the form row (authoritative)
    // rather than the `isPublic` render hint — a private form rendered inside
    // the app must not disable profile fields just because the caller passed
    // isPublic for spam protection, and vice versa.
    const formContext = useMemo(() => ({
        formID: form?.id || null,
        formIsPublic: !!form?.isPublic,
        // Server digest from get-public-form — NOT a client-computed timestamp.
        // A change means the agreement/consent the signer saw is stale.
        contentRevision: form?.contentRevision || null,
        onSignaturePending,
        signatureResetEpoch,
        // Repeat instances render with loggedInUser=null (no autofill target)
        // — the viewer's directory permission still applies to profile fields.
        viewerCanViewPeople: !!loggedInUser?.can?.('View People'),
    }), [form?.id, form?.isPublic, form?.contentRevision, onSignaturePending, signatureResetEpoch, loggedInUser]);

    // Public spam-protection: stamp a load timestamp on mount.
    useEffect(() => {
        if(isPublic){
            setFormLoadedAt(Date.now());
        }
    }, [isPublic]);

    const setValue = useCallback((fieldId, value) => {
        setValues((prev) => ({ ...prev, [fieldId]: value }));
        setErrors((prev) => {
            if(!prev[fieldId]) return prev;
            const next = { ...prev };
            delete next[fieldId];
            return next;
        });
    }, []);

    const addRepeatInstance = useCallback((section) => {
        const nextIndex = (repeats[section.id] || 0) + 1;
        setRepeats((prev) => ({ ...prev, [section.id]: nextIndex }));
        setValues((prev) => {
            const next = { ...prev };
            for (const field of section.fields || []) {
                next[getRepeatFieldId(field.fieldId, section.id, nextIndex)] = field.defaultValue ?? null;
            }
            return next;
        });
    }, [repeats]);

    const removeRepeatInstance = useCallback((sectionId, index) => {
        const oldCount = repeats[sectionId] || 0;
        if (oldCount === 0) return;
        setRepeats((prev) => ({ ...prev, [sectionId]: Math.max(0, oldCount - 1) }));
        setValues((prev) => {
            const section = sections.find((s) => s.id === sectionId);
            if (!section) return prev;
            const next = { ...prev };
            for (let i = index; i <= oldCount; i++) {
                for (const field of section.fields || []) {
                    const key = getRepeatFieldId(field.fieldId, sectionId, i);
                    if (i > index) {
                        next[getRepeatFieldId(field.fieldId, sectionId, i - 1)] = next[key];
                    }
                    delete next[key];
                }
            }
            return next;
        });
    }, [repeats, sections]);

    const copyFromPrevious = useCallback((sectionId, fieldId, index) => {
        const prevKey = index === 1 ? fieldId : getRepeatFieldId(fieldId, sectionId, index - 1);
        const currentKey = getRepeatFieldId(fieldId, sectionId, index);
        setValues((prev) => ({ ...prev, [currentKey]: prev[prevKey] ?? null }));
    }, []);

    /**
     * Evaluates a section's `condition` ({ fieldId, operator, value }) against
     * the current values. Only `eq` is supported in v1. Returns true when the
     * section should be shown.
     */
    const isSectionVisible = useCallback((section) => {
        if(!section.condition) return true;
        if(section.condition.operator !== 'eq') return true;
        const current = values[section.condition.fieldId];
        return current === section.condition.value;
    }, [values]);

    /**
     * Runs client-side validation over base fields and every repeated-section
     * instance. Error keys match the synthetic field IDs used for rendering.
     */
    const validate = useCallback(() => {
        const next = {};
        for (const section of sections) {
            if (!isSectionVisible(section)) continue;
            for (const field of (section.fields || [])) {
                const err = validateFieldValue(field, values[field.fieldId]);
                if (err) next[field.fieldId] = err;
            }

            const repeatCount = repeats[section.id] || 0;
            if (section.allowAddMore && repeatCount > 0) {
                for (let i = 1; i <= repeatCount; i++) {
                    for (const field of (section.fields || [])) {
                        const instanceKey = getRepeatFieldId(field.fieldId, section.id, i);
                        const err = validateFieldValue(field, values[instanceKey]);
                        if (err) next[instanceKey] = err;
                    }
                }
            }
        }
        return next;
    }, [sections, values, repeats, isSectionVisible]);

    const handleSubmit = useCallback(async () => {
        if(isReadOnly || submitting) return;

        // A signature pad is mid-export — block submit rather than sending a
        // draft without an image.
        if (pendingSignatures.current.size > 0) {
            setServerError('Still processing a signature — try again in a moment.');
            return;
        }

        // Honeypot: pass through to the server (server-side source of truth per plan §0).
        // The backend rejects if filled. We do NOT short-circuit client-side so bots
        // can't bypass the check by disabling JS.

        const validationErrors = validate();
        if(Object.keys(validationErrors).length > 0){
            setErrors(validationErrors);
            return;
        }
        setErrors({});

        // Build the submission payload. Base fields stay at the top level;
        // repeated-section instances are grouped under `__repeats__` so the
        // backend can validate and store each instance distinctly.
        const payload = {};
        const files = {};
        for (const section of sections) {
            if (!isSectionVisible(section)) continue;
            for (const field of (section.fields || [])) {
                if (field.hidden) continue;
                let value = values[field.fieldId];
                if (field.type === 'phone' && value) {
                    const normalized = normalizePhoneUS(value);
                    if (normalized) value = normalized;
                }
                payload[field.fieldId] = value ?? null;
                if (FILE_TYPES.has(field.type) && payload[field.fieldId] != null) {
                    files[field.fieldId] = payload[field.fieldId];
                }
            }

            const repeatCount = repeats[section.id] || 0;
            if (section.allowAddMore && repeatCount > 0) {
                payload.__repeats__ = payload.__repeats__ || {};
                payload.__repeats__[section.id] = [];
                for (let i = 1; i <= repeatCount; i++) {
                    const instance = {};
                    for (const field of (section.fields || [])) {
                        if (field.hidden) continue;
                        const instanceKey = getRepeatFieldId(field.fieldId, section.id, i);
                        let value = values[instanceKey];
                        if (field.type === 'phone' && value) {
                            const normalized = normalizePhoneUS(value);
                            if (normalized) value = normalized;
                        }
                        instance[field.fieldId] = value ?? null;
                    }
                    payload.__repeats__[section.id].push(instance);
                }
            }
        }

        // Attach public spam-protection metadata.
        if(isPublic){
            payload._meta = {
                submissionID: submissionID.current,
                formLoadedAt,
                captchaToken,
                honeypot,
            };
        }

        // contentRevision rides _meta so every submit call site can forward it
        // to the submit-form body — a stale/missing revision is a 409 when the
        // live schema has signature fields.
        payload._meta = {
            ...(payload._meta || {}),
            submissionID: submissionID.current,
            contentRevision: form?.contentRevision || null,
        };
        setSubmitting(true);
        setServerError(null);
        try {
            if (!submissionID.current) submissionID.current = await generateFormSubmissionID();
            payload._meta.submissionID = submissionID.current;
            const result = onSubmit ? await onSubmit(payload, files) : null;
            const resolved = result || {
                successMessage: form?.successMessage || 'Thanks! Your response has been recorded.',
                redirectUrl: null,
            };
            setSuccessResult(resolved);
            if (!resolved.payment || resolved.payment.snapshot?.channel !== 'electronic') onSuccess?.(resolved);

            if(resolved.redirectUrl){
                if(Platform.OS === 'web'){
                    window.location.href = resolved.redirectUrl;
                } else {
                    Linking.openURL(resolved.redirectUrl);
                }
            }
        } catch (err) {
            // 409 form_changed — the live schema/revision moved mid-fill. Keep
            // ordinary answers, drop signature drafts + consent (they refer to
            // terms that may no longer exist), and ask the caller to refetch.
            if (err?.status === 409 || err?.message === 'form_changed') {
                const signatureKeys = new Set();
                for (const section of sections) {
                    for (const field of (section.fields || [])) {
                        if (field.type === 'signature') signatureKeys.add(field.fieldId);
                    }
                }
                setValues((prev) => Object.fromEntries(
                    Object.entries(prev).filter(([key]) => !signatureKeys.has(key.split(':')[0])),
                ));
                pendingSignatures.current.clear();
                // Bump the epoch so mounted pads drop stale ink (the cleared
                // value alone doesn't reach the pad's internal stroke state).
                setSignatureResetEpoch((v) => v + 1);
                setErrors({});
                setServerError('This form was updated while you were filling it out. Your other answers were kept — please review the changes and sign again.');
                onFormChanged?.();
                return;
            }
            // Structured validation errors keyed by field/repeat key map back
            // to the fields; everything else stays a top-level callout.
            if (err?.fieldErrors && typeof err.fieldErrors === 'object') {
                setErrors((prev) => ({ ...prev, ...err.fieldErrors }));
            }
            setServerError(err?.message || 'Something went wrong submitting the form.');
        } finally {
            setSubmitting(false);
        }
    }, [isReadOnly, submitting, isPublic, honeypot, validate, values, sections, formLoadedAt, captchaToken, onSubmit, onSuccess, onFormChanged, form]);

    // Read-only / closed states.
    if(form?.status === 'closed' || form?.status === 'archived'){
        return (
            <View style={styles.wrapper}>
                <Callout type="warning" title={form.status === 'archived' ? 'Form Archived' : 'Form Closed'} message="This form is no longer accepting responses." />
            </View>
        );
    }

    if (successResult?.payment && successResult.payment.status !== 'NOT_REQUIRED') {
        const payment = successResult.payment;
        const selection = values[payment.snapshot?.fieldId];
        return (
            <View style={styles.wrapper}>
                <ScrollView contentContainerStyle={styles.scrollContent} keyboardShouldPersistTaps="handled">
                    <Text style={styles.title}>Your response is saved</Text>
                    <FormPaymentCheckout token={payment.token} context={form?.paymentContext} snapshot={payment.snapshot} initialSelection={selection} autoStart={selection?.channel === 'electronic'} onPaymentSubmitted={(current) => onSuccess?.({ ...successResult, payment: { ...payment, status: current.status } })} />
                </ScrollView>
            </View>
        );
    }

    if(successResult){
        return (
            <View style={styles.wrapper}>
                <Animated.View entering={FadeIn.duration(400)} style={styles.successContainer}>
                    <LottieView
                        ref={lottieRef}
                        autoPlay
                        loop={false}
                        style={styles.successLottie}
                        source={COMPLETE_LOTTIE}
                    />
                    <Text style={styles.successMessage}>
                        {successResult.successMessage}
                    </Text>
                    {loggedInUser && (
                        <SolidButton
                            text="Back to Reach"
                            onPress={() => {
                                if (Platform.OS === 'web') {
                                    // Inside an iframe embed (FormEmbed), navigating the
                                    // frame alone leaves the user trapped in the embed —
                                    // navigate the top-level window instead. Setting
                                    // top.location is allowed cross-origin.
                                    try {
                                        const target = (typeof window !== 'undefined' && window.parent !== window)
                                            ? window.top
                                            : window;
                                        target.location.href = '/';
                                    } catch {
                                        window.location.href = '/';
                                    }
                                } else {
                                    // Linking.openURL('/') is an invalid URL and fails
                                    // silently — route to the app home instead (mirrors
                                    // the back button on the public form route).
                                    router.replace('/(tabs)/(home)');
                                }
                            }}
                            variant="outlined"
                            size="md"
                            testID="form-back-to-reach"
                        />
                    )}
                </Animated.View>
            </View>
        );
    }

    // When `scrollable` is false the parent owns scrolling — render a plain
    // View so header/footer siblings outside FormView scroll with the form.
    const ScrollContainer = scrollable ? ScrollView : View;
    const scrollContainerProps = scrollable
        ? { style: styles.scroll, contentContainerStyle: styles.scrollContent, keyboardShouldPersistTaps: 'handled' }
        : { style: styles.scrollContent };

    return (
        <View style={[styles.wrapper, !scrollable && styles.wrapperStatic]}>
            <ScrollContainer {...scrollContainerProps}>
                {(!!form?.title || !!form?.description) && (
                    <Animated.View entering={FadeIn.duration(400)} style={styles.header}>
                        <View style={styles.headerAccent} />
                        <View style={styles.headerText}>
                            {!!form?.title && <Text style={styles.title}>{form.title}</Text>}
                            {!!form?.description && <Text style={styles.description}>{form.description}</Text>}
                        </View>
                    </Animated.View>
                )}

                {/* Form-level documents (waivers, schedules). Rendered in fill
                    AND submission modes — review screens should show what the
                    respondent was offered. Signed URLs minted on tap only. */}
                <FormResources
                    // The public route identifies the form by its opaque
                    // publicId — formID calls are authenticated-only. The row's
                    // own publicId column is deliberately ignored: it exists on
                    // draft rows too, and in-app viewers have a session anyway.
                    formID={publicId ? null : (form?.id || null)}
                    publicId={publicId}
                    paths={form?.resourcePaths}
                    resources={form?.resources}
                />

                {sections.map((section, sIndex) => {
                    if (!isSectionVisible(section)) return null;
                    const sectionFields = (section.fields || []).filter((f) => !f.hidden);
                    if (sectionFields.length === 0) return null;
                    const repeatCount = repeats[section.id] || 0;

                    return (
                        <React.Fragment key={section.id}>
                            <Animated.View
                                entering={FadeIn.duration(400).delay(150 + sIndex * 80)}
                                style={styles.section}
                            >
                                {!!section.title && <Text style={styles.sectionTitle}>{section.title}</Text>}
                                {!!section.description && <Text style={styles.sectionDescription}>{section.description}</Text>}
                                {sectionFields.map((field) => (
                                    <FieldRenderer
                                        key={field.id || field.fieldId}
                                        field={field}
                                        value={values[field.fieldId]}
                                        onChange={(val) => setValue(field.fieldId, val)}
                                        error={errors[field.fieldId] || null}
                                        disabled={isReadOnly || submitting}
                                        renderMode={renderMode}
                                        formContext={formContext}
                                        paymentContext={form?.paymentContext}
                                        loggedInUser={loggedInUser}
                                    />
                                ))}
                            </Animated.View>

                            {section.allowAddMore && (
                                <>
                                    {repeatCount > 0 && Array.from({ length: repeatCount }, (_, i) => i + 1).map((i) => (
                                        <Animated.View
                                            key={`${section.id}:${i}`}
                                            entering={FadeIn.duration(400).delay(150 + (sIndex + i) * 80)}
                                            style={styles.section}
                                        >
                                            <View style={styles.repeatHeader}>
                                                <Text style={styles.repeatTitle(colors, fonts)}>
                                                    {section.title ? `${section.title} (${i + 1})` : `Entry ${i + 1}`}
                                                </Text>
                                                {!isReadOnly && (
                                                    <TouchableOpacity onPress={() => removeRepeatInstance(section.id, i)} hitSlop={12}>
                                                        <Text style={styles.removeText(colors)}>Remove</Text>
                                                    </TouchableOpacity>
                                                )}
                                            </View>
                                            {!!section.description && <Text style={styles.sectionDescription}>{section.description}</Text>}
                                            {sectionFields.map((field) => {
                                                const instanceField = {
                                                    ...field,
                                                    id: `${field.id || field.fieldId}:${i}`,
                                                    fieldId: getRepeatFieldId(field.fieldId, section.id, i),
                                                };
                                                return (
                                                    <View key={instanceField.id} style={styles.fieldInstance}>
                                                        {!isReadOnly && !COPY_PREVIOUS_EXCLUDED.has(field.type) && (
                                                            <TouchableOpacity
                                                                style={styles.copyBtn}
                                                                onPress={() => copyFromPrevious(section.id, field.fieldId, i)}
                                                                testID={`field-copy-prev-${instanceField.id}`}
                                                            >
                                                                <Unicon name="copy-outline" library="ionicon" size={14} colorVariant="forms" />
                                                                <Text style={styles.copyText(colors, fonts)}>Copy from previous</Text>
                                                            </TouchableOpacity>
                                                        )}
                                                        <FieldRenderer
                                                            field={instanceField}
                                                            value={values[instanceField.fieldId]}
                                                            onChange={(val) => setValue(instanceField.fieldId, val)}
                                                            error={errors[instanceField.fieldId] || null}
                                                            disabled={isReadOnly || submitting}
                                                            renderMode={renderMode}
                                                            formContext={formContext}
                                                            loggedInUser={null}
                                                        />
                                                    </View>
                                                );
                                            })}
                                        </Animated.View>
                                    ))}
                                    <TouchableOpacity
                                        style={[styles.addAnotherBtn(colors), isReadOnly && { opacity: 0.5 }]}
                                        onPress={() => addRepeatInstance(section)}
                                        disabled={isReadOnly}
                                        testID={`section-add-another-${section.id}`}
                                    >
                                        <Unicon name="add-outline" library="ionicon" size={20} colorVariant="forms" />
                                        <Text style={styles.addAnotherText(colors, fonts)}>
                                            {section.addMoreLabel || 'Add Another'}
                                        </Text>
                                    </TouchableOpacity>
                                </>
                            )}
                        </React.Fragment>
                    );
                })}

                {/* Honeypot: hidden from real users, visible to bots. Web-only
                    — public forms are served via the web route, and a raw
                    <input> isn't available on native. */}
                {isPublic && Platform.OS === 'web' && (
                    <View style={styles.honeypot} aria-hidden="true" pointerEvents="auto">
                        <Text style={styles.honeypotLabel}>Website</Text>
                        <input
                            type="text"
                            tabIndex={-1}
                            autoComplete="off"
                            value={honeypot}
                            onChange={(e) => setHoneypot(e.target.value)}
                            style={{ width: '100%', padding: 4 }}
                        />
                    </View>
                )}

                {/* hCaptcha slot for public forms. */}
                {isPublic && renderCaptcha ? renderCaptcha() : null}

                {!!serverError && <Callout type="error" message={serverError} />}

                {!isReadOnly && (
                    <Animated.View entering={FadeIn.duration(400).delay(150 + sections.length * 80)}>
                        <SolidButton
                            testID="form-submit-button"
                            text={form?.submitButtonText || 'Submit'}
                            onPress={handleSubmit}
                            processing={submitting}
                            type="forms"
                            size="lg"
                            width="100%"
                        />
                    </Animated.View>
                )}
            </ScrollContainer>
        </View>
    );
};

const styles = StyleSheet.create(({ colors, fonts, spacing, typography, shadows, roundness }) => ({
    wrapper: {
        flex: 1,
        width: '100%',
        maxWidth: 640,
        alignSelf: 'center',
        // padding: spacing.md,
    },
    wrapperStatic: {
        // flex: 1 would collapse inside a parent ScrollView with no definite height.
        // flex: 0 (basis 0%) also collapses on web — use flexGrow: 0 + flexBasis: auto
        // so the wrapper sizes to its content.
        flexGrow: 0,
        flexBasis: 'auto',
    },
    scroll: {
        flex: 1,
        width: '100%',
    },
    scrollContent: {
        gap: spacing.md,
        paddingHorizontal: spacing.xs,
        // Web: room for sticky submit footer. Mobile: room for native tab bar
        // (iOS 26+ floating tabs) — use a safe-area-aware value via env().
        paddingBottom: Platform.select({
            web: 120,
            default: 140,
        }),
    },
    header: {
        flexDirection: 'row',
        gap: spacing.sm,
        paddingBottom: spacing.sm,
    },
    headerAccent: {
        width: 4,
        borderRadius: 2,
        backgroundColor: colors.forms,
        flexShrink: 0,
    },
    headerText: {
        flex: 1,
        gap: 4,
    },
    title: {
        fontSize: typography.h1,
        fontFamily: fonts.bold,
        color: colors.text,
        lineHeight: typography.h1 * 1.2,
    },
    description: {
        fontSize: typography.body,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        lineHeight: typography.body * 1.4,
    },
    section: {
        backgroundColor: colors.surface,
        borderRadius: roundness.lg,
        padding: spacing.md,
        boxShadow: shadows.card,
        gap: spacing.xs,
    },
    sectionTitle: {
        fontSize: typography.h2,
        fontFamily: fonts.bold,
        color: colors.text,
    },
    sectionDescription: {
        fontSize: typography.caption,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    honeypot: {
        // Visually hidden but present in the DOM for bots to fill.
        position: 'absolute',
        left: -9999,
        top: -9999,
        width: 1,
        height: 1,
        overflow: 'hidden',
        opacity: 0,
    },
    honeypotLabel: {
        fontSize: 1,
    },
    successContainer: {
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: spacing.xl,
        gap: spacing.md,
    },
    successLottie: {
        width: 150,
        height: 150,
    },
    successMessage: {
        fontSize: typography.h2,
        fontFamily: fonts.heading,
        color: colors.text,
        textAlign: 'center',
        maxWidth: 480,
    },
    repeatHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingBottom: 4,
    },
    repeatTitle: (colors, fonts) => ({
        fontSize: typography.h2,
        fontFamily: fonts.bold,
        color: colors.text,
    }),
    removeText: (colors) => ({
        color: colors.error,
        fontSize: 13,
    }),
    addAnotherBtn: (colors) => ({
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        paddingVertical: 10,
        paddingHorizontal: 12,
        borderRadius: 10,
        borderWidth: 1,
        borderStyle: 'dashed',
        borderColor: colors.forms,
        backgroundColor: colors.surface,
    }),
    addAnotherText: (colors, fonts) => ({
        color: colors.forms,
        fontFamily: fonts.heading,
        fontSize: 14,
    }),
    fieldInstance: {
        position: 'relative',
    },
    copyBtn: {
        position: 'absolute',
        top: 18,
        right: 0,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        paddingVertical: 2,
        zIndex: 1,
        opacity: 1,
        _web:{
            transition: 'all 0.9s',
            _hover: {
                opacity: 0.7,
            }
        }
    },
    copyText: (colors, fonts) => ({
        color: colors.forms,
        fontFamily: fonts.heading,
        fontSize: 12,
    }),
}));

export default FormView;
